/**
 * Answer Engine citation semantics — the single definition of what a citation,
 * an other source, and a verified competitor are.
 *
 * The old shape mixed three different observations into two fields: `cited`
 * meant "brand named in prose OR a URL was returned", and `competitorUrls`
 * labelled every non-target citation a competitor, so directories, review
 * marketplaces, publications and social networks were reported as competitors
 * and produced competitor penalties and actions.
 *
 * New runs emit four independent fields. This module also carries the readers
 * that let every consumer render historical records without re-creating the
 * mixed semantics: a legacy `competitorUrls` array is read as other sources,
 * because that is what it actually contained.
 *
 * Pure types + pure functions only: client components import this too.
 */

export interface AnswerEngineProviderCitation {
  /** The target brand or domain appeared in the provider's prose answer. */
  brandMentioned: boolean
  /** A provider-returned source URL on the target domain. Null when absent. */
  directCitationUrl: string | null
  /** Every other cited URL. Sources, not competitors, unless verified below. */
  otherSourceUrls: string[]
  /**
   * Cited URLs matching a competitor domain the caller explicitly supplied and
   * verified. Never inferred: an empty verified list means an empty array here.
   */
  verifiedCompetitorUrls: string[]
}

interface LegacyProviderShape {
  brandMentioned?: unknown
  directCitationUrl?: unknown
  otherSourceUrls?: unknown
  verifiedCompetitorUrls?: unknown
  cited?: unknown
  citedUrl?: unknown
  competitorUrls?: unknown
}

function readUrlList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
}

function readUrl(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

/**
 * Normalize one provider record, current or historical, into the current
 * contract. Legacy records never gain verified competitors: their
 * `competitorUrls` were unverified non-target citations.
 */
export function readProviderCitation(
  provider: unknown
): AnswerEngineProviderCitation {
  const record = (provider || {}) as LegacyProviderShape
  const isCurrent =
    'brandMentioned' in record ||
    'directCitationUrl' in record ||
    'otherSourceUrls' in record

  if (isCurrent) {
    return {
      brandMentioned: record.brandMentioned === true,
      directCitationUrl: readUrl(record.directCitationUrl),
      otherSourceUrls: readUrlList(record.otherSourceUrls),
      verifiedCompetitorUrls: readUrlList(record.verifiedCompetitorUrls),
    }
  }

  // Historical shape. `cited` conflated prose mentions with returned URLs, so
  // it is the closest available evidence of a mention and nothing stronger.
  return {
    brandMentioned: record.cited === true,
    directCitationUrl: readUrl(record.citedUrl),
    otherSourceUrls: readUrlList(record.competitorUrls),
    verifiedCompetitorUrls: [],
  }
}

/** True when the provider evidences the page at all (URL citation or mention). */
export function hasCitationEvidence(
  citation: AnswerEngineProviderCitation
): boolean {
  return citation.directCitationUrl !== null || citation.brandMentioned
}

interface LegacyResultShape {
  otherSourceUrls?: unknown
  verifiedCompetitorUrls?: unknown
  competitorUrls?: unknown
}

/**
 * Result-level citation view. Historical `competitorUrls` degrade to other
 * sources so no consumer keeps calling an unverified domain a competitor.
 */
export function readAnswerEngineSources(result: unknown): {
  otherSourceUrls: string[]
  verifiedCompetitorUrls: string[]
} {
  const record = (result || {}) as LegacyResultShape
  if ('otherSourceUrls' in record || 'verifiedCompetitorUrls' in record) {
    return {
      otherSourceUrls: readUrlList(record.otherSourceUrls),
      verifiedCompetitorUrls: readUrlList(record.verifiedCompetitorUrls),
    }
  }
  return {
    otherSourceUrls: readUrlList(record.competitorUrls),
    verifiedCompetitorUrls: [],
  }
}
